import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ButtonGroup, Form, InputGroup, Nav, Table } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SubtleBadge from 'components/common/SubtleBadge';
import { toneFor, VERDICT, BRAND } from './auditTones';
import { displayDates } from './auditFormat';

/**
 * The findings themselves.
 *
 * Laid out the way the sign-off spreadsheet is: the exported row, ours directly
 * beneath it, and every cell of our row coloured by how well the two agree.
 * Reading down a column tells you where a field is consistently wrong; reading
 * across a pair tells you whether one order is right.
 *
 * Two rules keep that readable rather than a chequerboard:
 *
 *  - the EXPORT row is one continuous muted band across all its cells. Left
 *    white it alternated with our coloured row and the eye zigzagged down the
 *    table instead of reading each pair as a unit.
 *  - only OUR row carries the verdict colour, and it is the one marked with the
 *    brand accent, because it is our data being audited - what the reviewer has
 *    to act on.
 *
 * The default filter is "needs review": of the matched rows, only some have real
 * disagreements, and opening on all of them buries the actual work.
 */

const FILTERS = [
  { key: 'REVIEW', label: 'Needs review' },
  { key: 'SYSTEMIC', label: 'Known gaps only' },
  { key: 'AGREE', label: 'Agrees' },
  { key: 'ALL', label: 'All' }
];

const matchesFilter = (pair, filter) => {
  if (filter === 'ALL') return true;
  if (filter === 'REVIEW') return pair.verdict === 'FIELD_MISMATCH';
  if (filter === 'SYSTEMIC') return pair.verdict === 'SYSTEMIC_ONLY';
  return pair.verdict === 'EXACT_MATCH';
};

// An empty value is background, not content. Repeating the word "(blank)" down
// every column of the export row made the emptiness the loudest thing on screen.
const Empty = () => <span style={{ color: '#c6d0e0' }}>—</span>;

const EXPORT_BAND = 'rgba(120,132,156,0.055)';

const OurCell = ({ cell }) => {
  const tone = toneFor(cell.status);
  const value = displayDates(String(cell.mym || '').trim());
  return (
    <td
      className="fs-10"
      style={{ backgroundColor: tone.bg, color: tone.color, whiteSpace: 'nowrap' }}
      title={cell.note ? `${tone.label} — ${displayDates(cell.note)}` : tone.label}
    >
      {value || <span style={{ opacity: 0.65 }}>not held</span>}
      {cell.note && (
        <FontAwesomeIcon icon="info-circle" className="ms-1" style={{ fontSize: '0.7em', opacity: 0.7 }} />
      )}
    </td>
  );
};

OurCell.propTypes = { cell: PropTypes.object.isRequired };

const PairedTable = ({ result }) => {
  const [filter, setFilter] = useState('REVIEW');
  const [query, setQuery] = useState('');

  const fields = (result.columns && result.columns.compared) || [];

  const counts = useMemo(() => {
    const c = { ALL: result.pairs.length, REVIEW: 0, SYSTEMIC: 0, AGREE: 0 };
    result.pairs.forEach(p => {
      if (p.verdict === 'FIELD_MISMATCH') c.REVIEW += 1;
      else if (p.verdict === 'SYSTEMIC_ONLY') c.SYSTEMIC += 1;
      else c.AGREE += 1;
    });
    return c;
  }, [result.pairs]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return result.pairs.filter(p => {
      if (!matchesFilter(p, filter)) return false;
      if (!q) return true;
      const haystack = [p.person, String(p.prescriptionId), ...p.cells.map(c => c.export), ...p.cells.map(c => c.mym)]
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [result.pairs, filter, query]);

  // Every row carries the same verdict under a single-verdict filter, so the
  // column is a wall of identical badges telling the reviewer nothing. It earns
  // its place only in the "All" view, where verdicts actually differ.
  const showVerdict = filter === 'ALL';

  const headCell = extra => ({
    className: 'fs-11 text-uppercase text-600 fw-medium py-2',
    style: { whiteSpace: 'nowrap', ...extra }
  });

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <ButtonGroup size="sm">
          {FILTERS.map(f => (
            <Button
              key={f.key}
              variant={filter === f.key ? 'primary' : 'falcon-default'}
              onClick={() => setFilter(f.key)}
            >
              {f.label}
              <span className="ms-2 opacity-75">{counts[f.key]}</span>
            </Button>
          ))}
        </ButtonGroup>
        <InputGroup size="sm" style={{ maxWidth: 280 }}>
          <InputGroup.Text className="bg-body-tertiary">
            <FontAwesomeIcon icon="search" className="text-600" />
          </InputGroup.Text>
          <Form.Control
            type="search"
            placeholder="Search person, drug or ID"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </InputGroup>
      </div>

      {visible.length === 0 ? (
        <div className="text-center text-600 py-5 border rounded-3">
          <FontAwesomeIcon icon="search" className="d-block mx-auto mb-2 fs-6" style={{ opacity: 0.35 }} />
          Nothing to show for this filter.
        </div>
      ) : (
        <div className="table-responsive rounded-3 border" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Table size="sm" className="mb-0 align-middle" style={{ minWidth: 1100 }}>
            <thead>
              <tr className="bg-body-tertiary" style={{ position: 'sticky', top: 0, zIndex: 2 }}>
                <th {...headCell({ minWidth: 56 })}>Line</th>
                <th {...headCell({ minWidth: 190 })}>Person</th>
                {showVerdict && <th {...headCell({ minWidth: 116 })}>Verdict</th>}
                <th {...headCell({ minWidth: 112 })}>Source</th>
                {fields.map(f => (
                  <th key={f.id} {...headCell()} title={`Column "${f.header}" in your file`}>
                    {f.label}
                    {f.strictExempt && (
                      <FontAwesomeIcon
                        icon="asterisk"
                        className="ms-1"
                        style={{ fontSize: '0.6em', opacity: 0.6 }}
                        title="Always compared by meaning, even in strict mode"
                      />
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {visible.map((pair, i) => {
                const verdict = VERDICT[pair.verdict] || VERDICT.FIELD_MISMATCH;
                // A firm rule between pairs; the two rows inside a pair are only
                // separated by the band, so each pair reads as one block.
                const pairTop = i === 0 ? undefined : '2px solid #e3e6ed';
                return (
                  <React.Fragment key={`${pair.line}-${pair.prescriptionId}`}>
                    <tr style={{ backgroundColor: EXPORT_BAND }}>
                      <td rowSpan={2} className="text-500 fs-10 align-middle" style={{ borderTop: pairTop }}>
                        {pair.line}
                      </td>
                      <td rowSpan={2} className="align-middle" style={{ borderTop: pairTop }}>
                        <div className="fw-semibold fs-10">{pair.person}</div>
                        <div className="text-500 fs-11 mt-1">
                          Prescription ID {pair.prescriptionId}
                        </div>
                      </td>
                      {showVerdict && (
                        <td
                          rowSpan={2}
                          className="align-middle"
                          style={{ borderTop: pairTop, whiteSpace: 'nowrap' }}
                        >
                          <SubtleBadge bg={verdict.variant} pill>
                            {verdict.label}
                          </SubtleBadge>
                        </td>
                      )}
                      <td className="text-600 fs-11" style={{ whiteSpace: 'nowrap', borderTop: pairTop }}>
                        Export
                      </td>
                      {pair.cells.map(c => (
                        <td
                          key={c.fieldId}
                          className="text-700 fs-10"
                          style={{ whiteSpace: 'nowrap', borderTop: pairTop }}
                        >
                          {displayDates(String(c.export || '').trim()) || <Empty />}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td
                        className="fw-semibold fs-11"
                        style={{
                          whiteSpace: 'nowrap',
                          color: BRAND.base,
                          borderLeft: `3px solid ${BRAND.base}`
                        }}
                      >
                        MapYourMeds
                      </td>
                      {pair.cells.map(c => (
                        <OurCell key={c.fieldId} cell={c} />
                      ))}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      <div className="text-600 fs-11 mt-2">
        Showing {visible.length} of {result.pairs.length} matched rows. The shaded row is your
        uploaded file; the row marked{' '}
        <span className="fw-semibold" style={{ color: BRAND.base }}>
          MapYourMeds
        </span>{' '}
        is what we hold, coloured by how well the two agree — hover a cell for why.
        <FontAwesomeIcon icon="asterisk" className="mx-1" style={{ fontSize: '0.7em' }} />
        marks fields always compared by meaning rather than by wording.
      </div>
    </>
  );
};

PairedTable.propTypes = { result: PropTypes.object.isRequired };

const MissingTable = ({ rows }) => (
  <div className="table-responsive rounded-3 border">
    <Table size="sm" hover className="mb-0 align-middle">
      <thead>
        <tr className="bg-body-tertiary">
          <th className="fs-11 text-uppercase text-600 fw-medium py-2">Line</th>
          <th className="fs-11 text-uppercase text-600 fw-medium py-2">Person</th>
          <th className="fs-11 text-uppercase text-600 fw-medium py-2">Drug on the export</th>
          <th className="fs-11 text-uppercase text-600 fw-medium py-2">Why it is here</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={`${r.line}-${r.person}-${r.drug}`}>
            <td className="text-500 fs-10">{r.line}</td>
            <td className="fw-semibold fs-10">{r.person}</td>
            <td className="fs-10">{r.drug}</td>
            <td className="fs-10">
              <SubtleBadge bg={r.category === 'UNKNOWN_PERSON' ? 'danger' : 'warning'} pill>
                {r.category === 'UNKNOWN_PERSON' ? 'Unknown person' : 'No active prescription'}
              </SubtleBadge>
              <div className="text-600 fs-11 mt-1">{r.note}</div>
            </td>
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

MissingTable.propTypes = { rows: PropTypes.array.isRequired };

const ExtraTable = ({ rows }) => (
  <div className="table-responsive rounded-3 border">
    <Table size="sm" hover className="mb-0 align-middle">
      <thead>
        <tr className="bg-body-tertiary">
          <th className="fs-11 text-uppercase text-600 fw-medium py-2">Prescription ID</th>
          <th className="fs-11 text-uppercase text-600 fw-medium py-2">Person</th>
          <th className="fs-11 text-uppercase text-600 fw-medium py-2">Drug in MapYourMeds</th>
          <th className="fs-11 text-uppercase text-600 fw-medium py-2">Detail</th>
        </tr>
      </thead>
      <tbody>
        {rows.map(r => (
          <tr key={r.prescriptionId}>
            <td className="text-500 fs-10">{r.prescriptionId}</td>
            <td className="fw-semibold fs-10">{r.person}</td>
            <td className="fs-10">{r.drug}</td>
            <td className="text-600 fs-10">{r.detail || <Empty />}</td>
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
);

ExtraTable.propTypes = { rows: PropTypes.array.isRequired };

const EmptyState = ({ icon, children }) => (
  <div className="text-center text-600 py-5 border rounded-3">
    <FontAwesomeIcon icon={icon} className="d-block mx-auto mb-2 fs-6" style={{ opacity: 0.35 }} />
    {children}
  </div>
);

EmptyState.propTypes = { icon: PropTypes.string, children: PropTypes.node };

const AuditResultsTable = ({ result }) => {
  const [tab, setTab] = useState('paired');

  const tabs = [
    { key: 'paired', label: 'Compared side by side', count: result.pairs.length },
    { key: 'missing', label: 'Unmatched rows from your file', count: result.missing.length },
    { key: 'extra', label: 'Only in MapYourMeds', count: result.extra.length }
  ];

  return (
    <>
      <Nav variant="tabs" activeKey={tab} onSelect={k => setTab(k)} className="mb-3">
        {tabs.map(t => (
          <Nav.Item key={t.key}>
            <Nav.Link eventKey={t.key}>
              {t.label}
              <span className="ms-2 text-600">{t.count}</span>
            </Nav.Link>
          </Nav.Item>
        ))}
      </Nav>

      {tab === 'paired' && <PairedTable result={result} />}
      {tab === 'missing' &&
        (result.missing.length ? (
          <MissingTable rows={result.missing} />
        ) : (
          <EmptyState icon="check-circle">
            Every row on the export matched an active prescription.
          </EmptyState>
        ))}
      {tab === 'extra' &&
        (result.extra.length ? (
          <ExtraTable rows={result.extra} />
        ) : (
          <EmptyState icon="check-circle">
            Nothing active here is missing from the export.
          </EmptyState>
        ))}
    </>
  );
};

AuditResultsTable.propTypes = { result: PropTypes.object.isRequired };

export default AuditResultsTable;
